import React from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
// import { fg } from '@atlaskit/platform-feature-flags';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { isNonPublic } from '../../../../../../../common/utils/is-non-public';
// import { GitBuildStatus } from '../git-build-status';
// import type { BuildStatus } from '../git-build-status/types';
import type { searchGraphBranch_AtlassianSearchPage$key } from './__generated__/searchGraphBranch_AtlassianSearchPage.graphql';
import { messages } from './messages';

export const SearchGraphBranch = ({
	node,
	position,
	cloudId,
	primaryProduct,
	primarySubProduct,
	selectedProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchGraphResultProps<searchGraphBranch_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphBranch_AtlassianSearchPage on SearchResultGraphDocument {
				__typename
				id
				lastModifiedDate
				url
				permissionLevel
				entity {
					... on ExternalBranch {
						name
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalRepository {
								name
							}
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();

	const { lastModifiedDate, url, permissionLevel, entity } = data;
	const { name: branchName, container } = entity || {};
	const label = formatMessage(messages.resultLabel);

	// TODO: Wire up real build status data when GraphQL schema is available
	// const buildStatusComponent = isCiCdEnabled ? (
	// const isCiCdEnabled = fg('rovo_fps_enable_git_ui_ci_cd');
	// 	<GitBuildStatus build={placeholderBuildStatus} testId="branch-build-status" />
	// ) : null;
	const buildStatusComponent = null;

	const titleMetadataParts: [string, React.ReactNode][] = buildStatusComponent
		? [['build-status', buildStatusComponent]]
		: [];

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const headerMetadataParts = [
		label,
		['repository-name', container?.name ?? null],
		['padlock', padlockIcon],
		['last-modified', lastModifiedComponent],
	];

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const footerComponent = (
		<MentionedByLinkedResults
			productConfigs={productConfigs}
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			isAllProductSelected={!selectedProduct}
		/>
	);

	return (
		<SearchResult
			productConfigs={productConfigs}
			id={data.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={''}
			headerMetadataParts={headerMetadataParts}
			titleMetadata={
				<SearchMetadata
					productConfigs={productConfigs}
					parts={titleMetadataParts}
					displaySpacer={false}
				/>
			}
			title={branchName}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			position={position}
			partial={data}
			appMode={appMode}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			mainContentWidth={mainContentWidth}
			selectedSites={selectedSites}
			footer={footerComponent}
		/>
	);
};
